import assert from 'assert'
import { Array, Effect, Option } from 'effect'
import Buyer from 'features/NearbyBuyers/domain/Buyer'
import NearbyBuyersFilters from 'features/NearbyBuyers/domain/NearbyBuyersFilters'
import NearbyBuyersSorting from 'features/NearbyBuyers/domain/NearbyBuyersSorting'
import NearbyBuyerMachine from 'features/NearbyBuyers/machine/NearbyBuyersMachine/NearbyBuyerMachine'
import NearbyBuyerPropertyMachine from 'features/NearbyBuyers/machine/NearbyBuyersMachine/NearbyBuyerPropertyMachine'
import SaveNearbyBuyersToListMachine from 'features/NearbyBuyers/machine/NearbyBuyersMachine/SaveNearbyBuyersToListMachine'
import NearbyBuyerProperty from 'features/NearbyBuyers/NearbyBuyerProperty'
import GetNearbyBuyers from 'features/NearbyBuyers/repository/GetNearbyBuyers'
import NearbyBuyersRepo from 'features/NearbyBuyers/repository/NearbyBuyersRepo'
import NearbyBuyersRoutes from 'features/NearbyBuyers/routes/NearbyBuyersRoutes'
import { PropertyDetails } from 'features/PropertyDetails/domain/PropertyDetails.domain'
import Range from 'features/valueObjects/Range'
import { EffectLib } from 'libs/Effect'
import { ErrorLib } from 'libs/errors'
import OptionalTotalConnection from 'libs/OptionalTotalConnection'
import DraggablePanelMachine from 'presentation/components/DraggablePanel/DraggablePanelMachine'
import { MapLib } from 'presentation/components/Map/createMapStore'
import PropertyModalService from 'presentation/components/PropertyModal/PropertyModalService'
import { toast } from 'presentation/components/Toast'
import { ToastParams } from 'presentation/components/Toast/Toast.types'
import { DEFAULT_TOAST_ERROR_MESSAGE } from 'presentation/const/toast.const'
import { Breakpoint } from 'presentation/libs/breakpoint'
import { MapRef } from 'react-map-gl'
import { pipe } from 'remeda'
import AppCache from 'services/AppCache'
import AppRouter from 'services/AppRouter'
import { ActorLogicFrom, ActorRefFrom, and, assertEvent, assign, enqueueActions, fromPromise, setup } from 'xstate'

namespace NearbyBuyersMachine {
  export type Requirements = NearbyBuyersRepo | AppRouter | AppCache | PropertyModalService

  export type Emitted = { type: '' }

  export type CursorParams = {
    type: 'before' | 'after'
    cursor: string
  }

  export type Context = {
    filters: GetNearbyBuyers.Params['filters']
    sorting: GetNearbyBuyers.Params['sorting']
    cursorParams: Option.Option<CursorParams>
    subjectPropertyDetails: Option.Option<PropertyDetails.Property>
    subjectPropertyActorRef: Option.Option<NearbyBuyerPropertyMachine.ActorRef>
    data: Option.Option<{
      nearbyBuyerActorRefs: NearbyBuyerMachine.ActorRef[]
      nearbyBuyerPropertyActorRefs: NearbyBuyerPropertyMachine.ActorRef[]
      pageInfo: OptionalTotalConnection.PageInfo
    }>
    pageRange: Option.Option<Range>
    error: Option.Option<unknown>
    mapRef: Option.Option<MapRef>
    getListRef: Option.Option<() => HTMLDivElement>
    draggablePanelActorRef: DraggablePanelMachine.ActorRef
  }

  export type Event =
    | { type: 'load-property-succeeded', property: PropertyDetails.Property }
    | { type: 'load-property-failed', error: Error }
    | { type: 'set-partial-filters', filters: Partial<GetNearbyBuyers.Params['filters']> }
    | { type: 'set-sorting', sorting: GetNearbyBuyers.Params['sorting'] }
    | { type: 'go-to-next-page' }
    | { type: 'go-to-prev-page' }
    | { type: 'open-save-to-list-modal' }
    | { type: 'close-save-to-list-modal' }
    | { type: 'highlight-from-buyer', id: string }
    | { type: 'highlight-from-buyer-property', id: string }
    | { type: 'map-mounted', mapRef: MapRef }
    | { type: 'map-unmounted' }
    | { type: 'list-mounted', getListRef: () => HTMLDivElement }
    | { type: 'list-unmounted' }
    | { type: 'open-property-modal' } & PropertyModalService.OpenParams
    | { type: 'property-marker-clicked' }

  export type Tags = 'can-save-to-list'

  export type NearbyBuyerActorId = `nearbyBuyer-${string}`
  export type NearbyBuyerPropertyActorId = `nearbyBuyerProperty-${string}`
  export type Children = {
    saveToList: 'saveToList'
    draggablePanel: 'draggablePanel'
  }
  & Record<NearbyBuyerActorId, 'nearbyBuyer'>
  & Record<NearbyBuyerPropertyActorId, 'nearbyBuyerProperty'>

  export const make = () => Effect.gen(function * () {
    const runSync = yield * EffectLib.getRunSyncFromRuntime<AppRouter | PropertyModalService>()

    return setup({
      types: {
        context: {} as Context,
        events: {} as Event,
        emitted: {} as Emitted,
        tags: {} as Tags,
        children: {} as Children,
      },
      guards: {
        canFilterOrSort: ({ context }) => Option.isSome(context.subjectPropertyDetails),
        hasNoBuyerTypeSelected: ({ event, context }) => {
          assertEvent(event, 'set-partial-filters')
          const filters = {
            ...context.filters,
            ...event.filters,
          }

          return !filters.shouldIncludeFlippers && !filters.shouldIncludeLandLords
        },
        isNearbyBuyersResultPopulated: (_, { result }: { result: GetNearbyBuyers.Result }) =>
          GetNearbyBuyers.Result.$is('Populated')(result),
        isNearbyBuyersResultEmpty: (_, { result }: { result: GetNearbyBuyers.Result }) =>
          GetNearbyBuyers.Result.$is('Empty')(result),
        hasNextPage: ({ context }) => context.data.pipe(
          Option.map(data => data.pageInfo.hasNextPage),
          Option.getOrElse(() => false),
        ),
        hasPreviousPage: ({ context }) => context.data.pipe(
          Option.map(data => data.pageInfo.hasPreviousPage),
          Option.getOrElse(() => false),
        ),
        doesPropertyHaveLocation: (_, { property }: { property: PropertyDetails.Property }) =>
          property.location !== null,
      },
      actors: {
        getNearbyBuyers: yield * makeGetNearbyBuyersActor(),
        saveToList: yield * SaveNearbyBuyersToListMachine.make(),
        nearbyBuyer: yield * NearbyBuyerMachine.make(),
        nearbyBuyerProperty: yield * NearbyBuyerPropertyMachine.make(),
        draggablePanel: yield * DraggablePanelMachine.make(),
      },
      delays: {
        filterDebounce: 1000,
      },
      actions: {
        closeSaveToListModal: () => {
          NearbyBuyersRoutes.navigateToCloseModal('SAVE_TO_LIST').pipe(
            ErrorLib.tapCauseReporter,
            runSync,
          )
        },
        notify: (_, params: ToastParams) => {
          toast(params)
        },
        manageHighlightFromBuyer: ({ context }, { id }: { id: string }) => {
          if (Option.isNone(context.data)) return

          // unhighlight any other highlighted buyer
          context.data.value.nearbyBuyerActorRefs.forEach(actorRef => {
            const snap = actorRef.getSnapshot()
            const shouldHighlight = snap.context.buyer.id === id
            const isHighlighted = snap.matches({
              Initialized: { Hover: 'Highlighted' },
            })

            if (isHighlighted && !shouldHighlight)
              actorRef.send({ type: 'unhighlight' })
          })

          // highlight only the properties of the buyer
          const propertyIds = context.data.value.nearbyBuyerActorRefs
            .find(actorRef =>
              actorRef.getSnapshot().context.buyer.id === id,
            )
            ?.getSnapshot()
            .context.nearbyPropertyIDs || []

          context.data.value.nearbyBuyerPropertyActorRefs.forEach(actorRef => {
            const snap = actorRef.getSnapshot()
            const shouldHighlight = propertyIds.includes(snap.context.property.id)
            const isPropertyHighlighted = snap.matches({ Initialized: 'Highlighted' })

            if (shouldHighlight && !isPropertyHighlighted)
              actorRef.send({ type: 'highlight' })
            else if (!shouldHighlight && isPropertyHighlighted)
              actorRef.send({ type: 'unhighlight' })
          })
        },
        manageHighlightFromBuyerProperty: ({ context }, { id }: { id: string }) => {
          if (Option.isNone(context.data)) return

          // unhighlight any other highlighted property
          context.data.value.nearbyBuyerPropertyActorRefs.forEach(actorRef => {
            const snap = actorRef.getSnapshot()
            const isHighlighted = snap.matches({ Initialized: 'Highlighted' })
            const isMatch = snap.context.property.id === id

            if (isHighlighted && !isMatch)
              actorRef.send({ type: 'unhighlight' })
          })

          // highlight only the buyers of the property
          const buyerIds = context.data.value.nearbyBuyerPropertyActorRefs
            .find(actorRef =>
              actorRef.getSnapshot().context.property.id === id,
            )
            ?.getSnapshot()
            .context.nearbyBuyerIDs || []

          context.data.value.nearbyBuyerActorRefs.forEach(actorRef => {
            const snap = actorRef.getSnapshot()

            const shouldHighlight = buyerIds.includes(snap.context.buyer.id)
            const isBuyerHighlighted = snap.matches({
              Initialized: { Hover: 'Highlighted' },
            })

            if (shouldHighlight && !isBuyerHighlighted)
              actorRef.send({ type: 'highlight' })
            else if (!shouldHighlight && isBuyerHighlighted)
              actorRef.send({ type: 'unhighlight' })
          })
        },
        fitMarkersInMap: ({ context }) => {
          const mapRef = context.mapRef
          const data = context.data

          if (Option.isNone(mapRef) || Option.isNone(data)) return

          const coordinates = data.value.nearbyBuyerPropertyActorRefs
            .map(actorRef => actorRef.getSnapshot().context.property)
            .map(property => property.location)

          MapLib.fitMarkers(mapRef.value, {
            markers: coordinates,
            padding: MAP_PADDING,
          })
        },
        fitHighlightedMarkersInMapIfNotVisible: ({ context }) => {
          const mapRef = context.mapRef
          const data = context.data

          if (Option.isNone(mapRef) || Option.isNone(data)) return

          const highlightedCoordinates = data.value.nearbyBuyerPropertyActorRefs
            .filter(actorRef => actorRef.getSnapshot().matches({ Initialized: 'Highlighted' }))
            .map(actorRef => actorRef.getSnapshot().context.property.location)

          if (highlightedCoordinates.length === 0) return

          const bounds = mapRef.value.getMap().getBounds()
          const isAnyNotVisible = highlightedCoordinates.some(coordinate =>
            !bounds?.contains([coordinate.longitude, coordinate.latitude]))

          if (!isAnyNotVisible) return

          MapLib.fitMarkers(mapRef.value, {
            markers: highlightedCoordinates,
            padding: MAP_PADDING,
          })
        },
        scrollToHighlightedBuyerIfNotVisible: ({ context }) => {
          const getListRef = context.getListRef
          const data = context.data

          if (Option.isNone(getListRef) || Option.isNone(data)) return

          const buyer = data.value.nearbyBuyerActorRefs
            .find(actorRef => actorRef.getSnapshot().matches({
              Initialized: { Hover: 'Highlighted' },
            }))

          if (!buyer) return

          const getElement = buyer.getSnapshot().context.getElement

          if (Option.isNone(getElement)) return

          const element = getElement.value()

          if (!element) return

          scrollChildIntoParentView(getListRef.value(), element)
        },
        setSubjectPropertyData: ({ context }, { nearbyBuyerProperty }: { nearbyBuyerProperty: NearbyBuyerProperty }) => {
          const actorRef = context.subjectPropertyActorRef.pipe(Option.getOrThrow)
          actorRef.send({ type: 'set-data', data: nearbyBuyerProperty })
        },
        setSubjectPropertyDataFromDetails: ({ context }) => {
          const subjectDetails = context.subjectPropertyDetails.pipe(Option.getOrThrow)
          const location = subjectDetails.location
          assert(location !== null)

          return {
            nearbyBuyerProperty: {
              property: Buyer.Property.fromPropertyDetails(subjectDetails, {
                fallback: { location },
              }),
              nearbyBuyerIDs: [],
            },
          }
        },
        openPropertyModal: (_, params: PropertyModalService.OpenParams) =>
          Effect.gen(function * () {
            const api = yield * PropertyModalService
            api.open(params)
          }).pipe(
            ErrorLib.tapCauseReporter,
            runSync,
          ),
        openDraggablePanelIfMobile: ({ context }) => {
          const isMobile = Breakpoint.isCurrentAtLeast('mobSm')
          if (!isMobile) return
          const draggablePanelActorRef = context.draggablePanelActorRef
          draggablePanelActorRef.send({ type: 'open' })
        },
      },
    }).createMachine({
      /** @xstate-layout N4IgpgJg5mDOIC5QCMCuBPMAnWBZAhgMYAWAlgHZgDEsYALgLQAO+Wdp+ANgwGamd1ssANoAGALqJQTAPaxS7GeSkgAHogAcAdgA0IdIgCMokwDotG46IAsAVlFbDANlGGAvm71pMOAiQrUtIwsbBzcfAJCwoaSSCCy8orKceoI2noGCBZOprbGFgDMAExaTgCcFQUeXhhCfmSUNPQMsDKh5FBisdJyCqRKKqnp+pqWpmVOztZFDqIFthrVIN51RA3UALb4TAwbMqjkghBdKgl9AymIE4amGncFGmWGxQUFZUUZRkVLK75rAVQtjsDnsDkcTnEzklBldbFpTIZLHcNNYCk4ZqjPghEVoiuNbJMrFpbMUNLYfrU-v5GpxSLBGKDDpAIT1Ev1kqBUmVHKYitoLBpJoU5ljDBMNKZrKJyk47rLZVoqp5lpS8P8aXTGCD9kzjhJTr1oZcEBM8YZbBM4YYtHMtNMsTMnAVbgtrbjStoyRSfGrqdQZEwwORmFgA9g6OhdjIIFwWfFDeyYSaHrdHAUpcTEUVEaL06Y0dLRCiNDYCqILd7Vn6qExQ4G2JGtlgANbYBiEWmEVt67rxtkXTlXIo5UTDlGGVEkuG2B3msr50RlOYuG1FApaStU9amACS5D6XConBk+AgIbDDZaqEIhDAkGZ+shCYHakQC2d49txRxJKxZTs4xFEUEzTCYTjWNYm6+tue4HpwR4nmetYXhGV43neEDMjEBr9hyr4INYpSmE47rONycLlDOIzYs4ErWuWwFoiispQfUAS7vu7CHsep7nvWqE8Pg-APr2UKJsa763NYxjrt+2i-tRxgAZYJjlu8-4aEqNQ+mxlCmAAQqqAAiYDIDqhAUFAVCqPS+CCKY+A8IIWAABQRM5JlmQct4AJRUL80HsYZPqeeZllxmJL6pOBeJOC42jvIu1iaVijwSmUBKuk4CzonFrHqmABmqgAMohllUBASiFRQABuMitqYMB0AAcmArDIOgwVRI+rLnHhqSGGRLqLgUE7-uiE5YrY9iSuu0zZiSrxOPlfpFT6pWnuVlV6bV9WFU1rXtZ1qrRKJz79UYDiitYS68kUCwlja3JFit25dTgG0QOV2ChlgphMJwdk8G0GyNfQh1YB170iD1fZ9Um3Kii484WkxQGeqNkHKgFumFRDUMlYhkAAAoBqggNHFQUAyAwdA05QqjBPgMARedSaDeUw1lJj43ZtYWIyuYE4lq88xOmUizY6quOmPjx3rUTECk0w5N2ZAVM03TIZgDVzDM2ArO4ezQ1kiNY3WBN-PUSLuTlrifLZcOWgblLOkFbLbWQ-LQifSTZMU+rZBQMQtLB4wPChhsDC-Ib8PGoNFrmMB3PaOmRZOliFvwhoQFjXaEw2i72lVtucvQ77Sv+2rEBUEHIekGHvCR9HlJ8eG6Cx0ag40SR5gvCRHPJfdWLprYkqTLY0nDu8UrfK7JfsWXhOnn7KsBxAu4QJw-qBsGsD4DVYC0zTtL0lGMacJ34nd6UNwS3cFujtJJZOFijiiLchglASOd8loFSvUXp7AmCsV6VzXtXUwABlA+YAAAqMhiqalwNGLgAB5XeFUqqmF2g1feh8EFIPpFfKKiBiTzhtINN0hFcRXWtsYPu5tnaT0GnPYuW4gFHXLorZWqsjjQNgYQ5BqDOAYKDFQDscgj74KPlrU+DIREkIulkROlDBrOxoTMXQ1FSgf2djMB4bxJ5zQ8Mqcg0Y4AqBxgVHCcdu5vFnDcTSE4SIlHXI8Za88OF6VglxTgtiu74XmGPNcJFFTTQyk6Ncooix4iKMldcBiSKykluwwKelmoyHenA9AgZYBQLANvQgRwAnXyCS4XkaJnbBMiS8GJa5zDgQWEWL+wSi4qjdqtaGoVvKWVKaQgiM10RlnUuicsi5UrcmItyfko07R-0AXpbhm0Oj9OUc4EouQyS4kmHzaUZQ-zOFmkuNcrwlzonJF49JeNgHew+orAAohsJgEY1lJmnKYMCFgHBOmmDmRSth7oIllK4co7wv6XLSTLJeoDMLgL4ZAN5xpUSxTXIuYCYz7AHOonCeEWZiQ5zLMYL+iyblcOXnC3h69N7byRd3YlzppL2HmP3dE2LMi4mdNaCcJQMrZj5FjKF7sYU+x4VXfhMCCGIOERfMReFIrKOSh-YZ6LTTjPZWQ+Yd0XCTzihBbkgqOkLz0rgOk8gOilUIHZMpCqkzjl5IuEwDx-7DmcKKDExEiwS0BdJXE0xSWmAeVgX6dKgmDXMAXBw2hXCLkFKKF1kp7aY0IlorGHggA */
      id: 'buyersMachine',
      context: ({
        spawn,
      }) => ({
        filters: NearbyBuyersFilters.DEFAULT,
        sorting: NearbyBuyersSorting.DEFAULT,
        cursorParams: Option.none(),
        subjectPropertyDetails: Option.none(),
        subjectPropertyActorRef: Option.none(),
        data: Option.none(),
        pageRange: Option.none(),
        error: Option.none(),
        mapRef: Option.none(),
        getListRef: Option.none(),
        draggablePanelActorRef: spawn('draggablePanel', {
          id: 'draggablePanel',
          input: {
            parentRef: Option.none(),
            panelHeight: 72,
          },
        }),
      }),
      entry: [
        { type: 'closeSaveToListModal' },
      ],
      on: {
        'set-partial-filters': [{
          guard: and(['canFilterOrSort', 'hasNoBuyerTypeSelected']),
          target: '.NoBuyerTypesSelected',
          actions: [
            assign({
              filters: ({ event, context }) => ({
                ...context.filters,
                ...event.filters,
              }),
              pageRange: () => Option.none(),
              cursorParams: () => Option.none(),
            }),
          ],
        },
        {
          guard: 'canFilterOrSort',
          target: '.BuyersDebouncing',
          actions: [
            assign({
              filters: ({ event, context }) => ({
                ...context.filters,
                ...event.filters,
              }),
              pageRange: () => Option.none(),
              cursorParams: () => Option.none(),
            }),
          ],
        },
        ],
        'set-sorting': {
          guard: 'canFilterOrSort',
          target: '.BuyersDebouncing',
          actions: [
            assign({
              sorting: ({ event }) => event.sorting,
              pageRange: () => Option.none(),
              cursorParams: () => Option.none(),
            }),
          ],
        },
        'map-mounted': {
          actions: [
            assign({
              mapRef: ({ event }) => Option.some(event.mapRef),
            }),
            'fitMarkersInMap',
          ],
        },
        'map-unmounted': {
          actions: [
            assign({
              mapRef: () => Option.none(),
            }),
          ],
        },
        'list-mounted': {
          actions: [
            assign({
              getListRef: ({ event }) => Option.some(event.getListRef),
            }),
          ],
        },
        'list-unmounted': {
          actions: [
            assign({
              getListRef: () => Option.none(),
            }),
          ],
        },
        'open-property-modal': {
          actions: {
            type: 'openPropertyModal',
            params: ({ event }) => event,
          },
        },
        'property-marker-clicked': {
          actions: 'openDraggablePanelIfMobile',
        },
      },
      initial: 'Initial',
      states: {
        Initial: {
          on: {
            'load-property-succeeded': [
              {
                guard: {
                  type: 'doesPropertyHaveLocation',
                  params: ({ event }) => ({ property: event.property }),
                },
                target: 'BuyersLoading',
                actions: assign({
                  subjectPropertyDetails: ({ event }) => Option.some(event.property),
                  subjectPropertyActorRef: ({ event, spawn, self }) => {
                    assert(event.property.location !== null)
                    return Option.some(spawn('nearbyBuyerProperty', {
                      id: makeNearbyBuyerPropertyActorId(event.property.id),
                      input: {
                        parentActorRef: self,
                        property: Buyer.Property.fromPropertyDetails(event.property, {
                          fallback: { location: event.property.location },
                        }),
                        nearbyBuyerIDs: [],
                        nearbyDeals: [],
                        isSubject: true,
                      },
                    }))
                  },
                }),
              },
              {
                target: 'MissingLocation',
                actions: [
                  assign({
                    subjectPropertyDetails: ({ event }) => Option.some(event.property),
                  }),
                  {
                    type: 'notify',
                    params: {
                      status: 'error',
                      title: 'Property is missing location information',
                      message: 'We don’t support cash buyers for this property',
                    },
                  },
                ],
              },
            ],
            'load-property-failed': {
              target: 'Error',
              actions: [
                assign({
                  error: ({ event }) => Option.some(event.error),
                }),
                {
                  type: 'notify',
                  params: {
                    status: 'error',
                    title: 'Failed to load the property',
                    message: DEFAULT_TOAST_ERROR_MESSAGE,
                  },
                },
              ],
            },
          },
        },
        NoBuyerTypesSelected: {},
        BuyersDebouncing: {
          after: {
            filterDebounce: {
              target: 'BuyersLoading',
            },
          },
        },
        BuyersLoading: {
          invoke: {
            id: 'getNearbyBuyers',
            src: 'getNearbyBuyers',
            input: ({ context }): GetNearbyBuyers.Params => {
              const location = context.subjectPropertyDetails.pipe(
                Option.map(details => details.location),
                Option.getOrThrow,
              )

              assert(location !== null)

              return {
                location,
                filters: context.filters,
                sorting: context.sorting,
                ...context.cursorParams.pipe(
                  Option.map((cursorParams): Partial<GetNearbyBuyers.Params> =>
                    cursorParams.type === 'after'
                      ? { after: cursorParams.cursor, first: 50 }
                      : { before: cursorParams.cursor, last: 50 },
                  ),
                  Option.getOrElse(() => ({
                    first: 50,
                  })),
                ),
              }
            },
            onDone: [
              {
                guard: { type: 'isNearbyBuyersResultPopulated', params: ({ event }) => ({ result: event.output }) },
                target: 'NearbyBuyersLoadedPopulated',
                actions: [
                  // Stop revious buyer and property actors except the subject property
                  enqueueActions(({ enqueue, context }) => {
                    const prevData = context.data

                    if (Option.isSome(prevData)) {
                      prevData.value.nearbyBuyerActorRefs.forEach(actorRef =>
                        enqueue.stopChild(actorRef),
                      )

                      prevData.value.nearbyBuyerPropertyActorRefs.forEach(actorRef => {
                        if (actorRef.getSnapshot().context.isSubject) return
                        enqueue.stopChild(actorRef)
                      })
                    }
                  }),

                  // assign new buyer and property actors
                  enqueueActions(({ enqueue, context, event }) => {
                    const result = event.output

                    if (GetNearbyBuyers.Result.$is('Populated')(result)) {
                      const subjectPropertyActorRef = context.subjectPropertyActorRef.pipe(
                        Option.getOrThrow,
                      )

                      const subjectParcelId = subjectPropertyActorRef.getSnapshot().context.property.parcelId

                      const [
                        subjectPropertyFromResult,
                        nonSubjectPropertyFromResult,
                      ] = separateSubjectFromNearbyBuyerProperties(result.nearbyBuyerProperties, subjectParcelId)

                      enqueue.assign({
                        data: ({ spawn, self }) => {
                          const nearbyBuyerActorRefs = result.nearbyBuyersConnection.edges
                            .map(edge => spawn('nearbyBuyer', {
                              id: makeNearbyBuyerActorId(edge.node.buyer.id),
                              input: {
                                parentActorRef: self,
                                cursor: edge.cursor,
                                buyer: edge.node.buyer,
                                nearbyPropertyIDs: edge.node.nearbyPropertyIDs,
                              },
                            }))

                          const nonSubjectPropertyActorRefs = nonSubjectPropertyFromResult
                            .map(item => spawn('nearbyBuyerProperty', {
                              id: makeNearbyBuyerPropertyActorId(item.property.id),
                              input: {
                                parentActorRef: self,
                                ...item,
                              },
                            }))

                          const nearbyBuyerPropertyActorRefs = [
                            subjectPropertyActorRef,
                            ...nonSubjectPropertyActorRefs,
                          ]

                          return Option.some({
                            nearbyBuyerActorRefs,
                            nearbyBuyerPropertyActorRefs,
                            pageInfo: result.nearbyBuyersConnection.pageInfo,
                          })
                        },
                      })

                      if (Option.isSome(subjectPropertyFromResult)) {
                        enqueue({
                          type: 'setSubjectPropertyData',
                          params: {
                            nearbyBuyerProperty: subjectPropertyFromResult.value,
                          },
                        })
                      } else {
                        enqueue('setSubjectPropertyDataFromDetails')
                      }
                    } else {
                      enqueue.assign({
                        data: Option.none(),
                      })
                      enqueue('setSubjectPropertyDataFromDetails')
                    }
                  }),

                  assign({
                    pageRange: ({ event, context }) => {
                      const output = event.output
                      if (!GetNearbyBuyers.Result.$is('Populated')(output))
                        return context.pageRange

                      return context.pageRange.pipe(
                        Option.map(range => {
                          const resultCount = output.nearbyBuyersConnection.edges.length
                          const didGoToPrevPage = context.cursorParams.pipe(
                            Option.map(cursorParams => cursorParams.type === 'before'),
                            Option.getOrElse(() => false),
                          )

                          return didGoToPrevPage
                            ? [range[0] - resultCount, range[0] - 1] satisfies Range
                            : [range[1] + 1, range[1] + resultCount] satisfies Range
                        }),
                        Option.orElse(() => Option.some([1, output.nearbyBuyersConnection.edges.length] satisfies Range)),
                      )
                    },
                  }),
                ],
              },
              {
                guard: { type: 'isNearbyBuyersResultEmpty', params: ({ event }) => ({ result: event.output }) },
                target: 'NearbyBuyersLoadedEmpty',
                actions: [
                  enqueueActions(({ enqueue, context }) => {
                    const prevData = context.data

                    if (Option.isSome(prevData)) {
                      prevData.value.nearbyBuyerActorRefs.forEach(actorRef =>
                        enqueue.stopChild(actorRef),
                      )

                      prevData.value.nearbyBuyerPropertyActorRefs.forEach(actorRef =>
                        enqueue.stopChild(actorRef),
                      )
                    }
                  }),
                  assign({
                    data: () => Option.none(),
                  }),
                ],
              },
            ],
            onError: {
              target: 'Error',
              actions: [
                assign({
                  error: ({ event }) => Option.some(event.error),
                }),
                {
                  type: 'notify',
                  params: {
                    status: 'error',
                    title: 'Failed to access the cash buyers',
                    message: DEFAULT_TOAST_ERROR_MESSAGE,
                  },
                },
              ],
            },
          },
        },
        NearbyBuyersLoadedEmpty: {},
        NearbyBuyersLoadedPopulated: {
          entry: [
            'fitMarkersInMap',
          ],
          initial: 'Idle',
          states: {
            Idle: {
              tags: ['can-save-to-list'],
              on: {
                'open-save-to-list-modal': {
                  target: 'SaveToListModalOpen',
                },
              },
            },
            SaveToListModalOpen: {
              invoke: {
                id: 'saveToList',
                src: 'saveToList',
                onDone: {
                  target: 'Idle',
                },
              },
              on: {
                'close-save-to-list-modal': {
                  target: 'Idle',
                },
              },
            },
          },
          on: {
            'go-to-next-page': {
              guard: 'hasNextPage',
              target: 'BuyersLoading',
              actions: [
                assign({
                  cursorParams: ({ context }) => {
                    const data = context.data

                    if (Option.isNone(data)) return Option.none()

                    const lastBuyer = pipe(
                      data.value.nearbyBuyerActorRefs,
                      Array.last,
                    )

                    if (Option.isNone(lastBuyer)) return Option.none()

                    return Option.some({
                      type: 'after',
                      cursor: lastBuyer.value.getSnapshot().context.cursor,
                    })
                  },
                }),
              ],
            },
            'go-to-prev-page': {
              guard: 'hasPreviousPage',
              target: 'BuyersLoading',
              actions: [
                assign({
                  cursorParams: ({ context }) => {
                    const data = context.data

                    if (Option.isNone(data)) return Option.none()

                    const firstBuyer = pipe(
                      data.value.nearbyBuyerActorRefs,
                      Array.head,
                    )

                    if (Option.isNone(firstBuyer)) return Option.none()

                    return Option.some({
                      type: 'before',
                      cursor: firstBuyer.value.getSnapshot().context.cursor,
                    })
                  },
                }),
              ],
            },
            'highlight-from-buyer': {
              actions: [
                {
                  type: 'manageHighlightFromBuyer',
                  params: ({ event }) => ({ id: event.id }),
                },
                'fitHighlightedMarkersInMapIfNotVisible'],
            },
            'highlight-from-buyer-property': {
              actions: [
                {
                  type: 'manageHighlightFromBuyerProperty',
                  params: ({ event }) => ({ id: event.id }),
                },
                'scrollToHighlightedBuyerIfNotVisible',
              ],
            },
          },
        },
        MissingLocation: {},
        Error: {},
      },
    })
  })

  export type ActorRef = ActorRefFrom<
    Effect.Effect.Success<ReturnType<typeof make>>
  >

  export type ActorLogic = ActorLogicFrom<
    Effect.Effect.Success<ReturnType<typeof make>>
  >

  const makeGetNearbyBuyersActor = () => Effect.gen(function * (_) {
    const repo = yield * NearbyBuyersRepo
    const runPromise = yield * EffectLib.getRunPromiseFromRuntime<AppCache>()

    return fromPromise((params: { input: GetNearbyBuyers.Params }) =>
      repo.getNearbyBuyers(params.input).pipe(
        ErrorLib.tapCauseReporter,
        runPromise,
      ))
  })

  export const makeNearbyBuyerPropertyActorId = (propertyId: string): NearbyBuyersMachine.NearbyBuyerPropertyActorId =>
    `nearbyBuyerProperty-${propertyId}` as const

  export const makeNearbyBuyerActorId = (buyerId: string): NearbyBuyersMachine.NearbyBuyerActorId =>
    `nearbyBuyer-${buyerId}` as const

  const MAP_PADDING = {
    top: 80,
    left: 48,
    right: 48,
    bottom: 48,
  }

  const scrollChildIntoParentView = (parent: Element, child: Element) => {
    const parentRect = parent.getBoundingClientRect()
    const childRect = child.getBoundingClientRect()

    const offsetTop = childRect.top - parentRect.top + parent.scrollTop

    parent.scrollTo({
      top: offsetTop,
      behavior: 'smooth',
    })
  }

  type SubjectPropertyAndOthersTuple = [
    Option.Option<NearbyBuyerProperty>,
    NearbyBuyerProperty[],
  ]

  const separateSubjectFromNearbyBuyerProperties = (
    properties: NearbyBuyerProperty[],
    subjectParcelId: Option.Option<string>,
  ) => properties.reduce<SubjectPropertyAndOthersTuple>(([subject, nonSubject], item) => {
    const isSubject = Option.zipWith(
      subjectParcelId,
      item.property.parcelId,
      (subjectParcelId, parcelId) => subjectParcelId === parcelId,
    ).pipe(
      Option.getOrElse(() => false),
    )

    if (isSubject)
      return [Option.some(item), nonSubject]
    else
      return [subject, [...nonSubject, item]]
  }, [Option.none(), []])

}

export default NearbyBuyersMachine
